// import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import * as cookie from 'cookie';
import { debounce } from 'lodash';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import CoursesCard from '@/components/Courses/CoursesCard';
import CoursesListingPage from '@/components/CoursesV2/CoursesListing';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import HomepageSlider from '@/components/V2/HomePage/HomepageSlider';

import { detectCountry, getActiveCountries } from '@/services/globalCountry';
import { getAllProductsSSR } from '@/services/LandingPage';
import { formatEcomProductCourses } from '@/utils/ProductFormaterBooks';

import { tokenUpdate } from '../../atoms/authModalControl';
import { user } from '../../atoms/user';
import { getLoginToken } from '../../components/shared/getToken';
import { notify } from '../../components/shared/notify';
import HeroSection, {
} from '../../components/V2/HomePage/HeroSection';
import { EventDetails, logEvent } from '../../hooks/useClickEventTracking';
import { getMyCourses, syncCourses } from '../../services/Courses';
import { User } from '../../utils/interfaces/User';

export interface ICourse {
  id: string;
  course_image_url: string;
  name: string;
  slug: string;
  duration: number;
  completionPercentage?: number;
}

interface Props {
  ecomProducts: any[];
}

const Courses: React.FC<Props> = ({ ecomProducts }) => {
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [token, setToken] = useState('');
  // const screens = useBreakpoint();

  const [myCourseLoading, setMyCourseLoading] = useState(false);
  const [myCourses, setMyCourses] = useState<ICourse[]>([]);
  const [searchKeyword, setSearchKeyword] = useState<string>('');


  const router = useRouter();
  const keyWord = router.query.search;
  const userData: User | undefined = useRecoilValue(user);

  const handleGetMyCourse = async (searchQuery?: string) => {
    const token = await getLoginToken();
    if (token) {
      try {
        setMyCourseLoading(true);
        await handleSyncCourses();
        const response = await getMyCourses(searchQuery as string);
        const { data } = response;

        const formattedCourses = data?.map((course: any) => ({
          id: course?.course?.id,
          course_image_url: course?.course?.course_image_url,
          name: course?.course?.name,
          completionPercentage: course?.completion,
          slug: course?.course?.slug,
          duration: course?.course?.totalVideoDuration,
        }));
        setMyCourses(formattedCourses);
      } catch (error: any) {
        notify(error, 'error');
      } finally {
        setMyCourseLoading(false);
      }
    }
  };


  const handleSyncCourses = async () => {
    const token = await getLoginToken();
    if (token) {
      try {
        await syncCourses();
      } catch (error: any) {
        notify(error, 'error');
      }
    }
  };

  useEffect(() => {
    fetchData();

    if (isTokenChanged) {
      handleSyncCourses();
      handleGetMyCourse();
      setIsTokenChanged(false);
    }
  }, [isTokenChanged]);

  const fetchData = async () => {
    try {
      const token = await getLoginToken();
      setToken(token as string);
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };

  const handleSearch = async (keyword: string) => {
    if (keyword !== undefined) {
      handleGetMyCourse(keyword);
    } else {
      handleGetMyCourse();
    }
  };

  const delayedSearch = debounce((keyword: string) => {
    handleSearch(keyword);
    if (keyWord) {
      const eventParams: EventDetails = {
        event_name: 'search_keyword',
        source: 'courses',
        source_type: 'search',
        source_id: null,
        sub_source: null,
        sub_source_id: null,
        unit: 'string',
        value: keyword,
      };

      logEvent({
        eventDetails: eventParams,
        userData,
        pathname: router.pathname,
      });
    }
  }, 500);

  useEffect(() => {
    delayedSearch(searchKeyword);
    return () => {
      delayedSearch.cancel();
    };
  }, [searchKeyword]);

  useEffect(() => {
    setSearchKeyword(keyWord as string);
  }, [keyWord]);

  return (
    <>
      <Head>
        <title>Get Online Financial Courses | ZebraLearn</title>
        <meta
          name='description'
          content='Unlock your potential with our comprehensive range of online financial courses. Explore the best business courses online at ZebraLearn today.'
          key='description'
        />
      </Head>
      <div className='flex flex-col'>
        <div className='mt-0 mb-5 md:mb-14 '>
          <HeroSection />
        </div>
        <ShouldRender check={myCourses.length || myCourseLoading}>
          <HomepageSlider
            loading={!!myCourses?.length}
            heading='Continue Learning'
            subHeading='Go to Library'
            subHeadingLink='/library/?tab=3'
            total={myCourses?.length || 0}
            imageWidths={[120, 460, 460]}
          >
            <CoursesCard
              courses={myCourses}
              loading={myCourseLoading}
              token={token}
              isMyCourse={true}
            />
          </HomepageSlider>
        </ShouldRender>
        <CoursesListingPage
          isMyCourses={myCourses.length > 0}
          ecomProducts={ecomProducts}
        />
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  try {
    const { req } = ctx;

    const rawCookies = req.headers.cookie || '';
    const parsedCookies = cookie.parse(rawCookies);

    let country = parsedCookies['country']?.trim();

    if (!country) {
      const [activeCountries, detectedCode] = await Promise.all([
        getActiveCountries(),
        detectCountry(req),
      ]);

      if (activeCountries.length > 0) {
        const fallback = activeCountries.find((c: any) => c.code === 'IN');
        const found =
          activeCountries.find((c: any) => c.code === detectedCode) || fallback;

        country = found?.code || 'IN';
      }
    }

    const allCoursesData = await getAllProductsSSR({
      page: 1,
      limit: 9999,
      category_id: 5,
      country: country as string,
    });

    const formattedecomProductsData = formatEcomProductCourses(
      allCoursesData || [],
    );

    return {
      props: {
        ecomProducts: formattedecomProductsData,
      },
    };
  } catch (error) {
    return {
      props: {
        ecomProducts: [],
      },
    };
  }
};

export default Courses;
